// GeoJSON converters bridging the editor's pre-grouped `AreaDisplayItem` lists
// (memoized by the parent — see the seam's own doc comment on `borderDisplay`)
// straight into MapLibre features, without re-running `groundShapes`'s union —
// that work is already done. Pure functions only — no React, no I/O.

import type { AreaDisplayItem } from '../../lib/areaGrouping'
import { ringsToPolygons } from '../../lib/polygonUnion'
import { resolveHuntBorderColor } from '../../lib/mapBorders'
import type { GeoArea, GeoPt } from '../../lib/types'
import type { GeoJsonFeatureCollection, GeoJsonMultiPolygonFeature } from '../../lib/mapGeoJson'

type GeoJsonPos = [number, number]
const toPos = (p: GeoPt): GeoJsonPos => [p.lon, p.lat]

function closedRing(ring: readonly GeoPt[]): GeoJsonPos[] {
  const coords = ring.map(toPos)
  const [fx, fy] = coords[0]
  const [lx, ly] = coords[coords.length - 1]
  return fx === lx && fy === ly ? coords : [...coords, [fx, fy]]
}

/** True when an item is mid-edit (vertex handles or a reshape draft own it)
 *  and must therefore render as nothing here — the same `isEditing || isVertex`
 *  gate `FullscreenMapCanvas` applies before drawing a border/sub-area/forbidden
 *  shape. */
export function isAreaItemHidden(
  item: AreaDisplayItem<GeoArea>,
  opts: { editingId?: string; vertexId?: string },
): boolean {
  if (item.isGroup) return false
  const memberId = item.members[0].id
  return memberId === opts.editingId || memberId === opts.vertexId
}

export type GroundKind = 'border' | 'subarea' | 'forbidden'

/** The visible-layer FeatureCollection for one ground kind: every non-hidden
 *  item (groups included — they render, just aren't selectable). `border`
 *  features carry a resolved `color` property (team → own → default
 *  precedence); the other kinds paint a fixed layer colour and need none. */
export function groundDisplayFeatureCollection(
  items: readonly AreaDisplayItem<GeoArea>[],
  kind: GroundKind,
  opts: { editingId?: string; vertexId?: string; teamColor?: string },
): GeoJsonFeatureCollection<GeoJsonMultiPolygonFeature> {
  const features: GeoJsonMultiPolygonFeature[] = []
  for (const item of items) {
    if (isAreaItemHidden(item, opts)) continue
    const polys = ringsToPolygons(item.rings, item.outerCount)
    if (polys.length === 0) continue
    const coordinates: GeoJsonPos[][][] = polys.map((poly) => poly.map(closedRing))
    const properties: Record<string, unknown> = {
      key: item.key,
      // Present only for a selectable (non-group) member — a group's own
      // composite key is never a valid selection target (onPolygonSelect is
      // only ever called with a real member id), so its `id` stays absent
      // rather than colliding with a `selectedId` filter.
      id: item.isGroup ? null : item.members[0].id,
    }
    if (kind === 'border') {
      properties.color = resolveHuntBorderColor({ areaColor: item.color, teamColor: opts.teamColor })
    }
    features.push({ type: 'Feature', properties, geometry: { type: 'MultiPolygon', coordinates } })
  }
  return { type: 'FeatureCollection', features }
}

/** The combined hit-test FeatureCollection: only selectable (non-group,
 *  non-hidden) items, each carrying `{ kind, id }` so the click router can
 *  dispatch `onPolygonSelect(kind, id)` directly off the queried feature.
 *  Feature order is the caller's responsibility (see `GROUND_HIT_ID`'s doc
 *  comment on the fixed subarea → forbidden → border priority). */
export function groundHitFeatures(
  items: readonly AreaDisplayItem<GeoArea>[],
  kind: GroundKind,
  opts: { editingId?: string; vertexId?: string },
): GeoJsonMultiPolygonFeature[] {
  const features: GeoJsonMultiPolygonFeature[] = []
  for (const item of items) {
    if (item.isGroup || isAreaItemHidden(item, opts)) continue
    const polys = ringsToPolygons(item.rings, item.outerCount)
    if (polys.length === 0) continue
    const coordinates: GeoJsonPos[][][] = polys.map((poly) => poly.map(closedRing))
    features.push({
      type: 'Feature',
      properties: { kind, id: item.members[0].id },
      geometry: { type: 'MultiPolygon', coordinates },
    })
  }
  return features
}
